import { t } from 'i18next';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { RECOGNITION_KEYS } from '../../../services/Recognition/hooks/useQuery';

import { Add, FilterAltOff, Sync } from '@mui/icons-material';
import { Box } from '@mui/material';

import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';
import ImageModalComponent from '../../../components/ImageModalComponent';
import ImageSimpleColumnComponent from '../../../components/ImageSimpleColumnComponent';
import TableComponent, { IColumnProps } from '../../../components/TableComponent';

import RecognitionNameCell from './_components/cells/RecognitionNameCell';
import RecognitionFilterModal from './_components/modals/RecognitionFilterModal';
import RecognitionsEditModal from './_components/modals/RecognitionsEditModal';
import SyncFacesModal from './_components/modals/SyncFacesModal';

import { useDebounce } from '../../../services/Recognition/hooks/useDebounce';
import { useDeleteRecognitionAsync, useLoadFirebaseImageMutation } from '../../../services/Recognition/hooks/useMutation';
import {
  useGetAllRecognitionsByLicenseCountQuery,
  useGetAllRecognitionsByLicenseQuery,
} from '../../../services/Recognition/hooks/useQuery';

import {
  IRecognitionDeleteParams,
  IRecognitionFilterParams,
  IRecognitionSimpleProps,
  IRecognitionTypes,
} from '../../../services/Recognition/types';
import { IPaginationParams } from '../../../services/types';
import { formatCnpj, formatCpf } from '../../../utils/formatters';
import { getRecognitionTypeLabel } from './utils';
import { useDeleteResponsibleAsync } from 'services/Recognition/Responsible/hooks/useMutation';
import { useDeleteStudentAsync } from 'services/Recognition/Student/hooks/useMutation';

const INITIAL_FILTER: IRecognitionFilterParams = {
  search: '',
  discriminator: undefined,
  isActive: undefined,
  temporary: undefined,
};

const INITIAL_PAGINATION: IPaginationParams = {
  pageSize: 10,
  pageNumber: 1,
};

const MemoizedTable = memo(TableComponent);

const RecognitionsPage = () => {
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState<IPaginationParams>(INITIAL_PAGINATION);
  const [filter, setFilter] = useState<IRecognitionFilterParams>(INITIAL_FILTER);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [editModal, setEditModal] = useState<{ isOpen: boolean; item?: IRecognitionSimpleProps }>({ isOpen: false });
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; item?: IRecognitionSimpleProps }>({ isOpen: false });
  const [imageModal, setImageModal] = useState({ isOpen: false, url: '' });

  const [isShowSyncFacesModal, setIsShowSyncFacesModal] = useState(false);
  const [isVisibleRecognitionFilterModal, setIsVisibleRecognitionFilterModal] = useState(false);
  
  const { mutateAsync: deleteStudent, isLoading: isDeletingStudent } = useDeleteStudentAsync();
  const { mutateAsync: deleteResponsible, isLoading: isDeletingResponsible } = useDeleteResponsibleAsync();
  const { mutateAsync: deleteGeneric, isLoading: isDeletingGeneric } = useDeleteRecognitionAsync();

  const isDeletingAny = isDeletingStudent || isDeletingResponsible || isDeletingGeneric;

  const {
    data: recognitions,
    isLoading: isLoadingRecognitions,
  } = useGetAllRecognitionsByLicenseQuery(pagination, filter);

  const {
    data: recognitionsCount,
    isLoading: isLoadingRecognitionsCount,
  } = useGetAllRecognitionsByLicenseCountQuery(filter);

  const {
    mutateAsync: mutateDeleteRecognition,
    isLoading: isLoadingDeleteRecognition,
  } = useDeleteRecognitionAsync();

  const { mutate: loadImage } = useLoadFirebaseImageMutation();

  useEffect(() => {
    setFilter((prev) => {
      if (prev.search === debouncedSearchTerm) return prev;
      return { ...prev, search: debouncedSearchTerm };
    });
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, [debouncedSearchTerm]);

  const refetchRecognitionsList = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries([RECOGNITION_KEYS.ALL, RECOGNITION_KEYS.LIST]),
      queryClient.invalidateQueries([RECOGNITION_KEYS.ALL, RECOGNITION_KEYS.COUNT]),
    ]);
  }, [queryClient]);

  const handleFilterChange = useCallback((val: string) => {
    setSearchTerm(val);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilter(INITIAL_FILTER); 
    setSearchTerm(''); 
    setPagination(prev => ({ ...prev, pageNumber: 1 }));
  }, []);

  const handleOpenEditionModal = useCallback((id?: number) => {
    if (id && recognitions) {
      const item = recognitions.find((i) => i.id === id);
      setEditModal({ isOpen: true, item });
    } else {
      setEditModal({ isOpen: true, item: undefined }); 
    }
  }, [recognitions]);

  const handleCloseEditionModal = useCallback(() => {
    setEditModal({ isOpen: false, item: undefined });
  }, []);

  const handleSaveForm = useCallback(async () => {
    handleCloseEditionModal();
    await queryClient.invalidateQueries([RECOGNITION_KEYS.ALL]);
  }, [handleCloseEditionModal, queryClient]);

  const handleOpenDeletionModal = useCallback((id: number) => {
    if (recognitions) {
      const item = recognitions.find((i) => i.id === id);
      setDeleteModal({ isOpen: true, item });
    }
  }, [recognitions]);

  const handleCloseDeletionModal = useCallback(() => {
    setDeleteModal({ isOpen: false, item: undefined });
  }, []);

  const handleConfirmDeletion = useCallback(async () => {
    if (!deleteModal.item?.id) return;

    const id = deleteModal.item.id;
    const type = deleteModal.item.discriminator as IRecognitionTypes;

    try {
      if (type === IRecognitionTypes.STUDENT) {
        await deleteStudent(id);
      } 
      else if (type === IRecognitionTypes.RESPONSIBLE) {
        await deleteResponsible(id);
      } 
      else {
        await deleteGeneric({ id, type });
      }

      await queryClient.invalidateQueries([RECOGNITION_KEYS.ALL]);
    } catch (error) {
       console.error("Erro ao deletar", error);
    } finally {
      handleCloseDeletionModal(); 
    }
  }, [deleteModal.item, deleteStudent, deleteResponsible, deleteGeneric, handleCloseDeletionModal, queryClient]);

  const handleOpenImageModal = useCallback((url: string) => {
    if (!url) return;
    loadImage(url, {
      onSuccess: (tempUrl: string | null | undefined) => {
        if (!tempUrl) return;
        setImageModal({ isOpen: true, url: tempUrl });
      },
      onError: (err: unknown) => {
        console.error('Erro ao carregar imagem', err);
      },
    });
  }, [loadImage]);

  const handleCloseImageModal = useCallback(() => {
    setImageModal({ isOpen: false, url: '' });
  }, []);

  const handleOpenFilter = useCallback(() => {
    setIsVisibleRecognitionFilterModal(true);
  }, []);

  const handleToggleDeleteDialog = useCallback((val: boolean | ((prev: boolean) => boolean)) => {
    setDeleteModal((prev) => ({
      ...prev,
      isOpen: typeof val === 'function' ? val(prev.isOpen) : val,
    }));
  }, []);

  const columns: IColumnProps[] = useMemo(() => [
    {
      id: 'discriminator',
      label: t('recognitionPage.tableHeaders.type'),
      width: 0,
      align: 'center',
      render: (val: string) => <>{getRecognitionTypeLabel(val as IRecognitionTypes)}</>,
    },
    {
      id: 'name',
      label: t('recognitionPage.tableHeaders.name'),
      width: '22%',
      render: (_val: string, _index: number, row: IRecognitionSimpleProps) => <RecognitionNameCell row={row} />,
    },
    {
      id: 'cpf',
      label: t('recognitionPage.tableHeaders.CPF'),
      render: (val: string) => <>{val ? formatCpf(val) : t('common.notAvailable')}</>,
    },
    {
      id: 'cnpj',
      label: t('recognitionPage.tableHeaders.CNPJ'),
      render: (val: string) => <>{val ? formatCnpj(val) : t('common.notAvailable')}</>,
    },
    {
      id: 'image',
      label: t('recognitionPage.tableHeaders.image'),
      width: 0,
      align: 'center',
      render: (url: string) => (
        <ImageSimpleColumnComponent url={url} onClick={() => handleOpenImageModal(url)} />
      ),
    },
    {
      id: 'id',
      label: t('recognitionPage.tableHeaders.actions'),
      align: 'center',
      width: 0,
      render: (idVal: string) => {
        const numericId = Number(idVal);
        return (
          <ButtonActionsComponent
            key={numericId}
            isWithDefaultOptionsList
            handleDelete={() => handleOpenDeletionModal(numericId)}
            handleEdit={() => handleOpenEditionModal(numericId)}
          />
        );
      },
    },
  ], [handleOpenImageModal, handleOpenDeletionModal, handleOpenEditionModal, t]);

  const tableTitleButtons = useMemo(() => (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' }, 
        alignItems: { xs: 'flex-end', sm: 'center' }, 
        gap: 1.5,
      }}
    >
      <ButtonComponent 
        size="small"
        onClick={() => handleOpenEditionModal()} 
        startIcon={<Add />}
      >
        {t('recognitionPage.newFace')}
      </ButtonComponent>

      <ButtonComponent 
        color="secondary" 
        size="small"
        onClick={() => setIsShowSyncFacesModal(true)} 
        startIcon={<Sync />}
      >
        {t('recognitionPage.synchronization')}
      </ButtonComponent>

      <ButtonComponent 
        onClick={handleClearFilters} 
        size="small" 
        color="warning" 
        startIcon={<FilterAltOff />}
      >
        {t('recognitionPage.cleanFilters')}
      </ButtonComponent>
    </Box>
  ), [handleClearFilters, handleOpenEditionModal, t]);

  return (
    <>
      <MemoizedTable
        title={t('recognitionPage.title')}
        columns={columns}
        rows={recognitions ?? []}
        sortByName={'name'}
        onOpenFilter={handleOpenFilter} 
        totalCount={recognitionsCount}
        onChangeMainFilter={handleFilterChange}
        onSubmitMainFilter={refetchRecognitionsList}
        isLoading={isLoadingRecognitions || isLoadingRecognitionsCount}
        currentPageNumber={pagination.pageNumber}
        onChangePageOrRowsPerPage={setPagination} 
        searchValue={searchTerm}
        additionalTitleComponent={tableTitleButtons}
      />

      <ConfirmDeleteModalComponent
        isLoading={isDeletingAny}
        isVisibleDeleteDialog={deleteModal.isOpen}
        setIsVisibleDeleteDialog={handleToggleDeleteDialog}
        title={t('recognitionPage.deleteFace')}
        message={t('recognitionPage.areYouSureWantToDeleteFace', {
          name: deleteModal.item?.name,
        })}
        handleCancelDeletion={handleCloseDeletionModal}
        handleConfirmDeletion={handleConfirmDeletion}
      />

      <ImageModalComponent
        isOpen={imageModal.isOpen}
        handleClose={handleCloseImageModal}
        imageUrl={imageModal.url}
        title={t('recognitionPage.recognitionImage')}
      />

      <RecognitionsEditModal
        isOpen={editModal.isOpen}
        handleClose={handleCloseEditionModal}
        selectedRecognition={editModal.item}
        handleSaveForm={handleSaveForm}
      />

      <SyncFacesModal
        isOpen={isShowSyncFacesModal}
        setIsOpen={setIsShowSyncFacesModal}
        handleCreateRecognition={refetchRecognitionsList}
      />

      <RecognitionFilterModal
        currentFilter={filter}
        onFilter={(newFilterVals) => setFilter(newFilterVals as IRecognitionFilterParams)}
        isOpen={isVisibleRecognitionFilterModal}
        onClose={() => setIsVisibleRecognitionFilterModal(false)}
      />
    </>
  );
};

export default RecognitionsPage;